import React, { useState, useEffect, useContext } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUserPlus, 
    faUserMinus, 
    faCheck, 
    faTimes, 
    faClock,
    faUser,
    faEnvelope,
    faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { useCustomAlert } from './CustomAlert';

const JoinRequestsModal = ({ groupId, onClose }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingRequest, setProcessingRequest] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [requestToReject, setRequestToReject] = useState(null);

    useEffect(() => {
        loadJoinRequests();
    }, [groupId]);

    const loadJoinRequests = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_group_join_requests.php?group_id=${groupId}&user_id=${user.user_id}&status=pending`);
            const data = await response.json();
            
            if (data.success) {
                setRequests(data.requests);
            } else {
                setError(data.error || 'Failed to load join requests');
            }
        } catch (err) {
            setError('Failed to load join requests');
        } finally {
            setLoading(false);
        }
    };

    const handleApproveRequest = async (requestId) => {
        try {
            setProcessingRequest(requestId);
            const response = await fetch(`${backendUrl}/process_group_join_request.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    request_id: requestId,
                    action: 'approve',
                    admin_user_id: user.user_id
                })
            });

            const data = await response.json();
            
            if (data.success) {
                showAlert('success', 'Join request approved successfully!');
                loadJoinRequests(); // Reload requests
            } else {
                showAlert('error', data.error || 'Failed to approve request');
            }
        } catch (err) {
            showAlert('error', 'Failed to approve request');
        } finally {
            setProcessingRequest(null);
        }
    };

    const handleRejectRequest = async () => {
        if (!requestToReject) return;
        
        try {
            setProcessingRequest(requestToReject.id);
            const response = await fetch(`${backendUrl}/process_group_join_request.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    request_id: requestToReject.id,
                    action: 'reject',
                    admin_user_id: user.user_id,
                    rejection_reason: rejectionReason
                })
            });

            const data = await response.json();
            
            if (data.success) {
                showAlert('success', 'Join request rejected');
                setShowRejectModal(false);
                setRequestToReject(null);
                setRejectionReason('');
                loadJoinRequests(); // Reload requests
            } else {
                showAlert('error', data.error || 'Failed to reject request');
            }
        } catch (err) {
            showAlert('error', 'Failed to reject request');
        } finally {
            setProcessingRequest(null);
        }
    };

    const openRejectModal = (request) => {
        setRequestToReject(request);
        setShowRejectModal(true);
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRequestToReject(null);
        setRejectionReason('');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="modal-overlay">
                <div className="modal-content join-requests-modal">
                    <div className="modal-header">
                        <h3>Join Requests</h3>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="modal-body">
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="modal-overlay">
                <div className="modal-content join-requests-modal">
                    <div className="modal-header">
                        <h3>
                            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                            Join Requests ({requests.length})
                        </h3>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger">
                                {error}
                            </div>
                        )}
                        
                        {requests.length === 0 ? (
                            <div className="no-requests">
                                <FontAwesomeIcon icon={faUserPlus} size="3x" className="text-muted mb-3" />
                                <h5>No pending requests</h5>
                                <p className="text-muted">There are no pending join requests for this group.</p>
                            </div>
                        ) : (
                            <div className="requests-list">
                                {requests.map(request => (
                                    <div key={request.id} className="request-card">
                                        <div className="request-user-info">
                                            <div className="user-avatar">
                                                {request.profile_picture ? (
                                                    <img 
                                                        src={request.profile_picture} 
                                                        alt={request.username}
                                                        className="avatar-img"
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon icon={faUser} className="avatar-icon" />
                                                )}
                                            </div>
                                            <div className="user-details">
                                                <h5 className="user-name">
                                                    {request.first_name} {request.last_name}
                                                </h5>
                                                <p className="username">@{request.username}</p>
                                                <p className="user-email sr-only">
                                                    <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                                                    {request.email}
                                                </p>
                                                <p className="request-date">
                                                    <FontAwesomeIcon icon={faCalendar} className="me-1" />
                                                    Requested {formatDate(request.requested_at)}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="request-actions">
                                            <button 
                                                className="btn btn-success btn-sm"
                                                onClick={() => handleApproveRequest(request.id)}
                                                disabled={processingRequest === request.id}
                                            >
                                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                {processingRequest === request.id ? 'Processing...' : 'Approve'}
                                            </button>
                                            <button 
                                                className="btn btn-danger btn-sm"
                                                onClick={() => openRejectModal(request)}
                                                disabled={processingRequest === request.id}
                                            >
                                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                Reject
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Reject Confirmation Modal */}
            {showRejectModal && (
                <div className="modal-overlay">
                    <div className="modal-content reject-modal">
                        <div className="modal-header">
                            <h4>Reject Join Request</h4>
                            <button className="close-btn" onClick={closeRejectModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to reject <strong>{requestToReject?.first_name} {requestToReject?.last_name}</strong>'s request to join the group?</p>
                            
                            <div className="form-group">
                                <label htmlFor="rejectionReason">Rejection Reason (Optional)</label>
                                <textarea
                                    id="rejectionReason"
                                    className="form-control"
                                    rows="3"
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Provide a reason for rejection (optional)..."
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary"
                                onClick={closeRejectModal}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger"
                                onClick={handleRejectRequest}
                                disabled={processingRequest === requestToReject?.id}
                            >
                                {processingRequest === requestToReject?.id ? 'Processing...' : 'Reject Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AlertComponent />
        </>
    );
};

export default JoinRequestsModal;
